import { IResultData } from "@/domain/entities/shared/result";
import {
  IAnswerProp,
  ILineDemandSurvey,
  ILineDemandSurveyAnswer,
} from "@/domain/entities/survey";
import {
  ISurvey,
  ISurveyTargetLine,
} from "@/domain/entities/survey/survey.types";
import TYPES from "@/ioc/identifiers";
import { container } from "@/ioc/inversify.config";
import { ISurveyRepository } from "@/repositories/survey/surveyRepository.types";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { defineStore } from "pinia";

interface ISurveyState {
  withdrawalSurvey: ISurvey | undefined;
  complaintsSurvey: ISurvey | undefined;
  lineSurvey: ISurvey | undefined;
  errorMessage: string;
}

const surveyRepository = () => {
  return container.get<ISurveyRepository>(TYPES.ISurveyRepository);
};

export const useSurveyStore = defineStore("survey", {
  state: (): ISurveyState => {
    return {
      withdrawalSurvey: undefined,
      complaintsSurvey: undefined,
      lineSurvey: undefined,
      errorMessage: "",
    };
  },
  getters: {},
  actions: {
    async getWithdrawalSurveyContents(): Promise<IResultData<ISurvey>> {
      const withdrawalSurveyResult: IResultData<ISurvey> = {
        isSuccess: false,
      };

      await surveyRepository()
        .getWithdrawalSurveyContents()
        .then((result) => {
          result
            .map((survey) => {
              withdrawalSurveyResult.isSuccess = true;
              withdrawalSurveyResult.data = survey;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(withdrawalSurveyResult, error);
              withdrawalSurveyResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          withdrawalSurveyResult.errorMessage = error.message;
        });

      this.withdrawalSurvey = withdrawalSurveyResult.data;
      return withdrawalSurveyResult;
    },

    async requestWitdrawal(
      surveyId: string,
      answers: Map<number, Map<string, string>>
    ): Promise<IResultData<boolean>> {
      const requestWithdrwalResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await surveyRepository()
        .requestWitdrawal(surveyId, answers)
        .then((result) => {
          result
            .map((resData) => {
              requestWithdrwalResult.isSuccess = resData.isSuccess;
              requestWithdrwalResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(requestWithdrwalResult, error);
              requestWithdrwalResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          requestWithdrwalResult.errorMessage = error.message;
        });

      return requestWithdrwalResult;
    },

    async getComplaintsSurveyContents(): Promise<IResultData<ISurvey>> {
      const complaintSurveyResult: IResultData<ISurvey> = {
        isSuccess: false,
      };
      await surveyRepository()
        .getComplaintsSurveyContents()
        .then((result) => {
          result
            .map((survey) => {
              complaintSurveyResult.isSuccess = true;
              complaintSurveyResult.data = survey;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(complaintSurveyResult, error);
              complaintSurveyResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          complaintSurveyResult.errorMessage = error.message;
        });

      this.complaintsSurvey = complaintSurveyResult.data;
      return complaintSurveyResult;
    },

    async sendSurveyResult(
      surveyId: string,
      answers: Map<number, Map<string, string | string[]>>,
      turnId?: string,
      allocUid?: string
    ): Promise<IResultData<boolean>> {
      const sendSurveyResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await surveyRepository()
        .sendSurveyRequest(surveyId, answers, turnId, allocUid)
        .then((result) => {
          result
            .map((resData) => {
              sendSurveyResult.isSuccess = resData.isSuccess;
              sendSurveyResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(sendSurveyResult, error);
              sendSurveyResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          sendSurveyResult.errorMessage = error.message;
        });

      return sendSurveyResult;
    },

    async getServiceEvaluationContents(): Promise<IResultData<ISurvey>> {
      const serviceEvaluationResult: IResultData<ISurvey> = {
        isSuccess: false,
      };

      await surveyRepository()
        .getServiceEvaluationContents()
        .then((result) => {
          result
            .map((survey) => {
              serviceEvaluationResult.isSuccess = true;
              serviceEvaluationResult.data = survey;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(serviceEvaluationResult, error);
              serviceEvaluationResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          serviceEvaluationResult.errorMessage = error.message;
        });

      this.lineSurvey = serviceEvaluationResult.data;
      return serviceEvaluationResult;
    },

    async getSurveyTargetLines(
      surveyId: string
    ): Promise<IResultData<ISurveyTargetLine[]>> {
      const surveyLinesResult: IResultData<ISurveyTargetLine[]> = {
        isSuccess: false,
        data: [],
      };
      await surveyRepository()
        .getSurveyTargetLines(surveyId)
        .then((result) => {
          result
            .map((lines) => {
              surveyLinesResult.isSuccess = true;
              surveyLinesResult.data = lines;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(surveyLinesResult, error);
              surveyLinesResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          surveyLinesResult.errorMessage = error.message;
        });

      return surveyLinesResult;
    },

    async getLineDemandSurveyList(): Promise<IResultData<ILineDemandSurvey[]>> {
      const surveyListResult: IResultData<ILineDemandSurvey[]> = {
        isSuccess: false,
        data: [],
      };
      await surveyRepository()
        .getLineDemandSurveyList()
        .then((result) => {
          result
            .map((resData) => {
              surveyListResult.isSuccess = true;
              surveyListResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(surveyListResult, error);
              surveyListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          surveyListResult.errorMessage = error.message;
        });

      return surveyListResult;
    },

    async getLineDemandSurveyAnswer(
      surveyId: string
    ): Promise<IResultData<ILineDemandSurveyAnswer | undefined>> {
      const surveyAnswerResult: IResultData<
        ILineDemandSurveyAnswer | undefined
      > = {
        isSuccess: false,
        data: undefined,
      };
      await surveyRepository()
        .getLineDemandSurveyAnswer(surveyId)
        .then((result) => {
          result
            .map((resData) => {
              surveyAnswerResult.isSuccess = true;
              surveyAnswerResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(surveyAnswerResult, error);
              surveyAnswerResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          surveyAnswerResult.errorMessage = error.message;
        });

      return surveyAnswerResult;
    },

    async updateLineDemandSurveyAnswer(
      surveyId: string,
      answerProps: IAnswerProp[]
    ): Promise<IResultData<boolean>> {
      const updateResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };
      await surveyRepository()
        .updateLineDemandSurveyAnswer(surveyId, answerProps)
        .then((result) => {
          result
            .map((resData) => {
              updateResult.isSuccess = resData.isSuccess;
              updateResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateResult, error);
              updateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateResult.errorMessage = error.message;
        });

      return updateResult;
    },

    async deleteLineDemandSurveyAnswer(
      surveyId: string
    ): Promise<IResultData<boolean>> {
      const updateResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };
      await surveyRepository()
        .deleteLineDemandSurveyAnswer(surveyId)
        .then((result) => {
          result
            .map((resData) => {
              updateResult.isSuccess = resData.isSuccess;
              updateResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateResult, error);
              updateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateResult.errorMessage = error.message;
        });

      return updateResult;
    },
  },
});
